import React, { useId } from "react"
import { formatDate, formatScore } from "../../lib/format"

interface Point {
  id?: number
  score: number
  finished_at: string
}

/**
 * Evolução da nota ao longo dos simulados (área + linha, domínio fixo 0–10).
 * Série única → sem legenda; último ponto rotulado; <title> por ponto pro hover.
 */
export function ScoreTrend({ points }: { points: Point[] }) {
  const gradientId = useId()
  const W = 320
  const H = 132
  const padX = 16
  const padTop = 22
  const padBottom = 16
  const n = points.length
  const clamp = (s: number) => Math.max(0, Math.min(10, s))

  const x = (i: number) => (n === 1 ? W / 2 : padX + (i * (W - 2 * padX)) / (n - 1))
  const y = (s: number) => padTop + (1 - clamp(s) / 10) * (H - padTop - padBottom)

  const line = points.map((p, i) => `${x(i)},${y(p.score)}`).join(" ")
  const area = `${x(0)},${H - padBottom} ${line} ${x(n - 1)},${H - padBottom}`
  const midY = y(5)

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-36 w-full" role="img" aria-label="Evolução da nota nos últimos simulados">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--color-gold-500)" stopOpacity="0.24" />
          <stop offset="100%" stopColor="var(--color-gold-500)" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* referência da metade (5,0) */}
      <line x1={padX} y1={midY} x2={W - padX} y2={midY} stroke="var(--color-slate-200)" strokeDasharray="3 4" />
      <text x={W - padX} y={midY - 4} textAnchor="end" className="fill-navy-300 text-[9px]">5,0</text>

      <polygon points={area} fill={`url(#${gradientId})`} />
      <polyline
        points={line}
        fill="none"
        stroke="var(--color-gold-500)"
        strokeWidth={2.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />

      {points.map((p, i) => {
        const last = i === n - 1
        return (
          <g key={p.id ?? i}>
            <circle
              cx={x(i)}
              cy={y(p.score)}
              r={last ? 5 : 3.5}
              fill={last ? "var(--color-gold-600)" : "var(--color-gold-500)"}
              stroke="white"
              strokeWidth={last ? 2 : 1.5}
            >
              <title>{`${formatDate(p.finished_at)}: ${formatScore(p.score)}`}</title>
            </circle>
            {last && (
              <text x={x(i)} y={y(p.score) - 12} textAnchor="middle" className="fill-navy-900 text-[11px] font-bold">
                {formatScore(p.score)}
              </text>
            )}
          </g>
        )
      })}
    </svg>
  )
}
